import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useGoogleMaps } from '../components/providers/GoogleMapsProvider';
import type { LatLng } from '../components/shopper/DeliveryMap';
import {
  coordsFromAddress,
  coordsFromFirestoreLocation,
  formatAddressLine,
  geocodeAddress,
  type GeocodableAddress,
} from '../lib/geocode';

interface OrderMapSource {
  businessId?: string;
  deliveryAddress?: GeocodableAddress | null;
  driver?: { location?: { lat?: number; lng?: number } };
  uber?: {
    courierLocation?: { lat?: number; lng?: number };
  };
}

interface BusinessMapSource {
  location?: { lat?: number; lng?: number; latitude?: number; longitude?: number } | null;
  address?: GeocodableAddress | null;
  formattedAddress?: string | null;
}

export function useOrderMapLocations(
  order: OrderMapSource | null | undefined,
  businessOverride?: BusinessMapSource | null,
) {
  const { isLoaded } = useGoogleMaps();
  const [store, setStore] = useState<LatLng | null>(null);
  const [dropoff, setDropoff] = useState<LatLng | null>(null);
  const [geocoding, setGeocoding] = useState(false);
  const [business, setBusiness] = useState<BusinessMapSource | null>(businessOverride ?? null);

  const courier = useMemo((): LatLng | null => {
    if (!order) return null;
    const driverLoc = order.driver?.location;
    if (typeof driverLoc?.lat === 'number' && typeof driverLoc?.lng === 'number') {
      return { lat: driverLoc.lat, lng: driverLoc.lng };
    }
    const uberLoc = order.uber?.courierLocation;
    if (typeof uberLoc?.lat === 'number' && typeof uberLoc?.lng === 'number') {
      return { lat: uberLoc.lat, lng: uberLoc.lng };
    }
    return null;
  }, [order]);

  // Load business doc when not provided inline
  useEffect(() => {
    if (businessOverride !== undefined) {
      setBusiness(businessOverride);
      return;
    }
    const businessId = order?.businessId;
    if (!businessId) {
      setBusiness(null);
      return;
    }
    let cancelled = false;
    getDoc(doc(db, 'businesses', businessId))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        const data = snap.data();
        setBusiness({
          location: data?.location ?? null,
          address: data?.address ?? null,
          formattedAddress: data?.formattedAddress ?? null,
        });
      })
      .catch(() => {
        if (!cancelled) setBusiness(null);
      });
    return () => {
      cancelled = true;
    };
  }, [order?.businessId, businessOverride]);

  // Resolve store + dropoff (geocode when lat/lng missing)
  useEffect(() => {
    if (!order) {
      setStore(null);
      setDropoff(null);
      return;
    }

    const directDropoff = coordsFromAddress(order.deliveryAddress);
    if (directDropoff) {
      setDropoff(directDropoff);
    }

    const directStore =
      coordsFromFirestoreLocation(business?.location) ?? coordsFromAddress(business?.address);

    if (directStore) {
      setStore(directStore);
    }

    if (!isLoaded) return;

    let cancelled = false;
    setGeocoding(true);

    (async () => {
      const tasks: Promise<void>[] = [];

      if (!directDropoff) {
        const line = formatAddressLine(order.deliveryAddress);
        if (line) {
          tasks.push(
            geocodeAddress(line).then((pt) => {
              if (!cancelled && pt) setDropoff(pt);
            }),
          );
        }
      }

      if (!directStore) {
        const line = formatAddressLine(business?.address) || business?.formattedAddress || '';
        if (line) {
          tasks.push(
            geocodeAddress(line).then((pt) => {
              if (!cancelled && pt) setStore(pt);
            }),
          );
        }
      }

      await Promise.all(tasks);
      if (!cancelled) setGeocoding(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [order, business, isLoaded]);

  const hasAnyPoint = !!(store || dropoff || courier);

  return {
    store,
    dropoff,
    courier,
    isLoaded,
    geocoding,
    hasAnyPoint,
  };
}
